/**
 * The editor's Savings tab: the customer's consumption (bills, a kWh history, a yearly total or a Green Button file),
 * the tariff found for the address with an override, and the payment options (cash, loan, lease, PPA) with their
 * terms. Results follow the design live (the panels as they are now, saved or not): year-1 savings, the cash flow,
 * payback, ROI, IRR, bills before and after, and each option's monthly payment next to the monthly savings.
 * The inputs are kept per design (`design_savings_inputs`), saved as they change.
 */
import { useQuery } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { Alert, Badge, NumberInput, Select, Spinner, useToast } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";
import { money, num } from "../lib/format";
import { CashFlowChart } from "./CashFlowChart";
import { cleanOption, monthlyLine, PAYMENT_LABEL_KEY, PAYMENT_TYPES, paybackText, toFraction, toPercent, type PaymentType } from "./payments.ts";
import type { SaveState } from "./planHooks.ts";
import "./plan.css";

/** The twelve month names in the reader's language (`short`: "Jan", `narrow`: "J"). */
function useMonths(width: "short" | "narrow" = "short"): string[] {
  const intl = useIntlShape();
  return Array.from({ length: 12 }, (_, i) => intl.formatDate(new Date(2024, i, 15), { month: width }));
}
type Mode = "bills" | "kwh" | "annual" | "file";

function modeOf(c: S["ConsumptionInput"] | null | undefined): Mode {
  if (c?.kwh_history?.length) return "kwh";
  if (c?.annual_kwh) return "annual";
  return "bills";
}

export interface SavingsPanelProps {
  designId: string;
  inputs: S["SavingsRequest"];
  setInputs: (next: S["SavingsRequest"]) => void;
  saveState: SaveState;
  saveError: string | null;
  canEdit: boolean;
  result: S["SavingsResult"] | null;
  loading: boolean;
  error: string | null;
  selected: PaymentType;
  onSelect: (t: PaymentType) => void;
}

export function SavingsPanel(p: SavingsPanelProps) {
  const { inputs, setInputs, result } = p;
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const months = useMonths();
  const percent = (v: number, digits = 0) => intl.formatNumber(v / 100, { style: "percent", maximumFractionDigits: digits, minimumFractionDigits: digits });
  const [mode, setMode] = useState<Mode>(() => modeOf(inputs.consumption));
  const [fileInfo, setFileInfo] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lookup = useQuery({
    queryKey: ["tariff-lookup", p.designId],
    queryFn: () => unwrap(api.GET("/v1/electricity/tariffs/lookup", { params: { query: { design_id: p.designId } } })),
  });
  const currency = result?.currency ?? "USD";
  const consumption = inputs.consumption ?? {};
  const setConsumption = (c: S["ConsumptionInput"] | null) => {
    const { consumption: _, ...rest } = inputs;
    setInputs(c ? { ...rest, consumption: c } : rest);
  };
  const options: S["PaymentOption"][] = inputs.payment_options?.length ? inputs.payment_options : PAYMENT_TYPES.map((type) => ({ type }));
  const setOption = (type: PaymentType, patch: Partial<S["PaymentOption"]>) =>
    setInputs({ ...inputs, payment_options: options.map((o) => (o.type === type ? cleanOption({ ...o, ...patch }) : cleanOption(o))) });
  const toggleOption = (type: PaymentType, on: boolean) => {
    const next = on ? [...options, { type }].sort((a, b) => PAYMENT_TYPES.indexOf(a.type) - PAYMENT_TYPES.indexOf(b.type)) : options.filter((o) => o.type !== type);
    if (!next.length) return;
    setInputs({ ...inputs, payment_options: next.map(cleanOption) });
    if (!on && p.selected === type) p.onSelect(next[0]!.type);
  };

  const monthly = (key: "monthly_bills" | "kwh_history") => {
    const given = new Map((consumption[key] ?? []).map((m) => [m.month, m.amount]));
    const set = (month: number, v: number | null) => {
      const next = new Map(given);
      if (v == null || v <= 0) next.delete(month);
      else next.set(month, v);
      const list = [...next.entries()].sort((a, b) => a[0] - b[0]).map(([month, amount]) => ({ month, amount }));
      setConsumption(list.length ? { [key]: list, ...(key === "kwh_history" && consumption.daily_profile ? { daily_profile: consumption.daily_profile } : {}) } : null);
    };
    return (
      <div className="month-grid">
        {months.map((m, i) => (
          <label key={m}>
            <span>{m}</span>
            <NumberInput value={given.get(i + 1) ?? null} min={0} step={key === "monthly_bills" ? 1 : 10} onChange={(v) => set(i + 1, v)} placeholder="—" />
          </label>
        ))}
      </div>
    );
  };

  const readFile = async (file: File) => {
    try {
      const content = await file.text();
      const format: S["ConsumptionFileFormat"] = /\.csv$/i.test(file.name) || !content.trimStart().startsWith("<") ? "green_button_csv" : "green_button_xml";
      const parsed = await unwrap(api.POST("/v1/consumption/parse", { body: { format, content } }));
      setConsumption({ kwh_history: parsed.kwh_history, ...(parsed.daily_profile ? { daily_profile: parsed.daily_profile } : {}) });
      setMode("kwh");
      const info = t("editor.savings.fileInfo", { file: file.name, months: parsed.kwh_history.length, kwh: num(parsed.kwh_history.reduce((s, m) => s + m.amount, 0)) });
      setFileInfo(parsed.warnings.length ? `${info} · ${parsed.warnings[0]}` : info);
    } catch (e) {
      toast(errorMessage(e), true);
    }
  };

  const tariffValue = inputs.manual_price?.rate_per_kwh != null ? "manual" : inputs.tariff_id ?? "";
  const chosen = result?.options.find((o) => o.type === p.selected) ?? result?.options[0] ?? null;
  const years = (result?.assumptions.find((a) => a.key === "analysis_years")?.value as number | undefined) ?? 25;

  return (
    <section className="stack plan-panel">
      <div className="plan-status small muted">
        {p.canEdit
          ? p.saveState === "saving"
            ? t("editor.savings.status.saving")
            : p.saveState === "saved"
              ? t("editor.savings.status.saved")
              : p.saveState === "error"
                ? <span className="warn-text">{t("editor.savings.status.error", { error: p.saveError ?? "" })}</span>
                : t("editor.savings.status.idle")
          : t("editor.savings.status.viewOnly")}
      </div>

      {p.error && !p.loading && <Alert tone="warn" title={t("editor.savings.error")}>{p.error}</Alert>}
      {result ? (
        <Block title={p.loading ? t("editor.savings.resultsUpdating") : t("editor.savings.results")} className={p.loading ? "updating" : ""}>
          <div className="kpi-grid">
            <Kpi label={t("editor.savings.kpi.year1Savings")} value={money(result.year1_savings.mid, currency)} sub={result.year1_savings.low !== result.year1_savings.high ? t("editor.savings.range", { low: money(result.year1_savings.low, currency), high: money(result.year1_savings.high, currency) }) : undefined} />
            <Kpi label={t("editor.savings.kpi.payback")} value={chosen ? paybackText(t, chosen.payback_years, years) : "—"} />
            <Kpi label={t("editor.savings.kpi.roi")} value={chosen?.roi_percent != null ? percent(chosen.roi_percent) : "—"} />
            <Kpi label={t("editor.savings.kpi.irr")} value={chosen?.irr != null ? percent(chosen.irr * 100, 1) : "—"} />
          </div>
          <div className="bills-row small">
            <span>{t("editor.savings.billsYear")}</span>
            <b>{money(result.bills.annual_before, currency)}</b>
            <DirIcon>
              <span aria-hidden>→</span>
            </DirIcon>
            <b className="ok-text">{money(result.bills.annual_after, currency)}</b>
          </div>
          <MonthlyBills before={result.bills.monthly_before} after={result.bills.monthly_after} currency={currency} />
          {chosen && <CashFlowChart flows={chosen.cash_flows} currency={currency} />}
          {result.warnings.length > 0 && <p className="small muted">{result.warnings.map((w) => w.message).join(" · ")}</p>}
        </Block>
      ) : p.loading ? (
        <Spinner label={t("editor.savings.calculating")} />
      ) : null}

      <Block title={t("editor.savings.paymentOptions")}>
        {result && result.options.length > 0 && (
          <div className="pay-compare-scroll">
            <table className="pay-compare">
              <thead>
                <tr>
                  <th />
                  {result.options.map((o) => (
                    <th key={o.type} className={p.selected === o.type ? "on" : ""} onClick={() => p.onSelect(o.type)}>
                      {t(PAYMENT_LABEL_KEY[o.type])}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ["editor.savings.compare.payment", (o: S["PaymentResult"]) => money(monthlyLine(o).payment, currency), ""],
                    ["editor.savings.compare.savings", (o: S["PaymentResult"]) => money(monthlyLine(o).savings, currency), "ok-text"],
                    ["editor.savings.compare.net", (o: S["PaymentResult"]) => money(monthlyLine(o).net, currency), "net"],
                    ["editor.savings.compare.upfront", (o: S["PaymentResult"]) => money(o.upfront, currency), ""],
                    ["editor.savings.compare.payback", (o: S["PaymentResult"]) => (o.payback_years != null ? t("editor.savings.compare.paybackYears", { years: Math.round(o.payback_years * 10) / 10 }) : "—"), ""],
                  ] as [MessageKey, (o: S["PaymentResult"]) => string, string][]
                ).map(([label, value, tone]) => (
                  <tr key={label}>
                    <th>{t(label)}</th>
                    {result.options.map((o) => (
                      <td key={o.type} className={`${p.selected === o.type ? "on " : ""}${tone === "net" ? (monthlyLine(o).net >= 0 ? "ok-text" : "warn-text") : tone}`} onClick={() => p.onSelect(o.type)}>
                        {value(o)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pay-list">
          {PAYMENT_TYPES.map((type) => {
            const option = options.find((o) => o.type === type);
            return (
              <div key={type} className={`pay-card${p.selected === type ? " on" : ""}${option ? "" : " off"}`}>
                <label className="row pay-title">
                  <input type="checkbox" checked={!!option} onChange={(e) => toggleOption(type, e.target.checked)} />
                  <b>{t(PAYMENT_LABEL_KEY[type])}</b>
                  {!option && <span className="small muted">{t("editor.savings.notOffered")}</span>}
                </label>
                {option && <OptionFields option={option} currency={currency} onChange={(patch) => setOption(type, patch)} />}
              </div>
            );
          })}
        </div>
      </Block>
      <Block title={t("editor.savings.consumption")}>
        <div className="segmented full" role="tablist" aria-label={t("editor.savings.consumptionFrom")}>
          {(
            [
              ["bills", "editor.savings.mode.bills"],
              ["kwh", "editor.savings.mode.monthly"],
              ["annual", "editor.savings.mode.yearly"],
              ["file", "editor.savings.mode.upload"],
            ] as [Mode, MessageKey][]
          ).map(([m, label]) => (
            <button key={m} className={mode === m ? "on" : ""} onClick={() => setMode(m)} role="tab" aria-selected={mode === m}>
              {t(label)}
            </button>
          ))}
        </div>
        {mode === "bills" && (
          <>
            <p className="muted small">{t("editor.savings.billsHelp", { currency })}</p>
            {monthly("monthly_bills")}
          </>
        )}
        {mode === "kwh" && (
          <>
            <p className="muted small">{t("editor.savings.kwhHelp")}</p>
            {monthly("kwh_history")}
            {fileInfo && <p className="small">{fileInfo}</p>}
          </>
        )}
        {mode === "annual" && (
          <label className="row">
            <span className="small">{t("editor.savings.yearlyUse")}</span>
            <NumberInput value={consumption.annual_kwh ?? null} min={0} step={100} suffix="kWh" onChange={(v) => setConsumption(v && v > 0 ? { annual_kwh: v } : null)} />
          </label>
        )}
        {mode === "file" && (
          <div className="stack">
            <p className="muted small">{t("editor.savings.fileHelp")}</p>
            <input ref={fileRef} type="file" accept=".xml,.csv,text/xml,text/csv" hidden onChange={(e) => e.target.files?.[0] && readFile(e.target.files[0])} />
            <button className="btn" onClick={() => fileRef.current?.click()}>
              <Upload size={16} /> {t("editor.savings.uploadFile")}
            </button>
          </div>
        )}
        {result && (
          <p className="small">
            {t("editor.savings.annualKwh", { kwh: num(result.consumption.annual_kwh.mid) })}
            {result.consumption.annual_kwh.low !== result.consumption.annual_kwh.high && (
              <span className="muted"> {t("editor.savings.rangeParen", { low: num(result.consumption.annual_kwh.low), high: num(result.consumption.annual_kwh.high) })}</span>
            )}
            {result.consumption.source === "default" && <Badge tone="warn">{t("editor.savings.assumed")}</Badge>}
          </p>
        )}
      </Block>

      <Block title={t("editor.savings.tariff")}>
        {lookup.isLoading ? (
          <Spinner label={t("editor.loading")} />
        ) : lookup.data ? (
          <>
            <div className="small">
              <b>{result?.tariff.name ?? lookup.data.tariff.name}</b>
              {(result?.tariff.utility ?? lookup.data.tariff.utility) && <span className="muted"> · {result?.tariff.utility ?? lookup.data.tariff.utility}</span>}{" "}
              <Badge tone={result?.tariff_verified ?? lookup.data.verified ? "ok" : "warn"} plain>
                {result?.tariff_source ?? lookup.data.source}
              </Badge>
            </div>
            <Select
              ariaLabel={t("editor.savings.tariffLabel")}
              value={tariffValue}
              onChange={(v) => {
                if (v === "manual") setInputs({ ...inputs, tariff_id: null, manual_price: { ...(inputs.manual_price ?? {}), rate_per_kwh: inputs.manual_price?.rate_per_kwh ?? 0.2 } });
                else setInputs({ ...inputs, tariff_id: v || null, manual_price: inputs.manual_price ? { ...inputs.manual_price, rate_per_kwh: null } : null });
              }}
              options={[
                { value: "", label: t("editor.savings.tariffFound") },
                ...lookup.data.candidates.map((c) => ({ value: c.tariff_id, label: c.utility ? t("editor.savings.tariffWithUtility", { name: c.name, utility: c.utility }) : c.name })),
                { value: "manual", label: t("editor.savings.tariffCustom") },
              ]}
            />
            {tariffValue === "manual" && (
              <label className="row">
                <span className="small">{t("editor.savings.price")}</span>
                <NumberInput value={inputs.manual_price?.rate_per_kwh ?? null} min={0} step={0.01} suffix={`${currency}/kWh`} onChange={(v) => setInputs({ ...inputs, manual_price: { ...(inputs.manual_price ?? {}), rate_per_kwh: v } })} />
              </label>
            )}
            <label className="row">
              <span className="small">{t("editor.savings.priceRise")}</span>
              <NumberInput value={toPercent(inputs.utility_escalation)} min={-50} max={100} step={0.5} suffix="%" placeholder={t("editor.savings.fromSettings")} onChange={(v) => {
                  const { utility_escalation: _, ...rest } = inputs;
                  const fraction = toFraction(v);
                  setInputs(fraction == null ? rest : { ...rest, utility_escalation: fraction });
                }} />
            </label>
          </>
        ) : (
          <Alert tone="warn" title={t("editor.savings.noTariff")}>{errorMessage(lookup.error)}</Alert>
        )}
      </Block>

    </section>
  );
}

function OptionFields({ option, currency, onChange }: { option: S["PaymentOption"]; currency: string; onChange: (patch: Partial<S["PaymentOption"]>) => void }) {
  const t = useT();
  const settings = t("editor.savings.fromSettings");
  const years = t("editor.savings.unit.years");
  const perYear = t("editor.savings.unit.percentPerYear");
  const field = (label: string, node: ReactNode) => (
    <label className="pay-field">
      <span>{label}</span>
      {node}
    </label>
  );
  switch (option.type) {
    case "cash":
      return <p className="small muted pay-fields">{t("editor.savings.cashHelp")}</p>;
    case "loan":
      return (
        <div className="pay-fields">
          {field(t("editor.savings.field.apr"), <NumberInput value={toPercent(option.apr)} min={0} max={100} step={0.1} suffix="%" placeholder={settings} onChange={(v) => onChange({ apr: toFraction(v) })} />)}
          {field(t("editor.savings.field.term"), <NumberInput value={option.term_years ?? null} min={1} max={40} step={1} suffix={years} placeholder={settings} onChange={(v) => onChange({ term_years: v == null ? null : Math.round(v) })} />)}
          {field(t("editor.savings.field.downPayment"), <NumberInput value={option.down_payment_pct ?? null} min={0} max={100} step={5} suffix="%" placeholder="0" onChange={(v) => onChange({ down_payment_pct: v })} />)}
        </div>
      );
    case "lease":
      return (
        <div className="pay-fields">
          {field(t("editor.savings.field.monthly"), <NumberInput value={option.monthly_payment ?? null} min={0} step={5} suffix={currency} placeholder={settings} onChange={(v) => onChange({ monthly_payment: v })} />)}
          {field(t("editor.savings.field.escalator"), <NumberInput value={toPercent(option.escalator)} min={0} max={100} step={0.1} suffix={perYear} placeholder={settings} onChange={(v) => onChange({ escalator: toFraction(v) })} />)}
          {field(t("editor.savings.field.term"), <NumberInput value={option.term_years ?? null} min={1} max={40} step={1} suffix={years} placeholder={settings} onChange={(v) => onChange({ term_years: v == null ? null : Math.round(v) })} />)}
        </div>
      );
    case "ppa":
      return (
        <div className="pay-fields">
          {field(t("editor.savings.field.rate"), <NumberInput value={option.rate_per_kwh ?? null} min={0} step={0.01} suffix="/kWh" placeholder={settings} onChange={(v) => onChange({ rate_per_kwh: v })} />)}
          {field(t("editor.savings.field.escalator"), <NumberInput value={toPercent(option.escalator)} min={0} max={100} step={0.1} suffix={perYear} placeholder={settings} onChange={(v) => onChange({ escalator: toFraction(v) })} />)}
          {field(t("editor.savings.field.term"), <NumberInput value={option.term_years ?? null} min={1} max={40} step={1} suffix={years} placeholder={settings} onChange={(v) => onChange({ term_years: v == null ? null : Math.round(v) })} />)}
        </div>
      );
  }
}

function MonthlyBills({ before, after, currency }: { before: number[]; after: number[]; currency: string }) {
  const t = useT();
  const months = useMonths();
  const initials = useMonths("narrow");
  const max = Math.max(1, ...before, ...after);
  return (
    <div className="bill-bars" role="img" aria-label={t("editor.savings.billsChart")}>
      {before.map((b, i) => (
        <div key={i} className="bill-month" title={t("editor.savings.billMonth", { month: months[i]!, before: money(b, currency), after: money(after[i] ?? 0, currency) })}>
          <div className="bill-pair">
            <span className="bill-before" style={{ height: `${(b / max) * 100}%` }} />
            <span className="bill-after" style={{ height: `${(Math.max(0, after[i] ?? 0) / max) * 100}%` }} />
          </div>
          <small>{initials[i]}</small>
        </div>
      ))}
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="kpi">
      <span>{label}</span>
      <b>{value}</b>
      {sub && <small className="muted">{sub}</small>}
    </div>
  );
}

export function Block({ title, children, className, actions }: { title: ReactNode; children: ReactNode; className?: string; actions?: ReactNode }) {
  return (
    <div className={`side-card plan-block ${className ?? ""}`}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <b>{title}</b>
        {actions}
      </div>
      {children}
    </div>
  );
}
